import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Alert,
    Snackbar,
    MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getFirestore, collection, getDocs, query, orderBy, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';

const firestore = getFirestore();
const tiposCuadrilla = ['cuadrilla', 'deposito'];

function ListCuadrillas() {
    const [cuadrillas, setCuadrillas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCuadrilla, setEditingCuadrilla] = useState(null);
    const [formData, setFormData] = useState({
        numero: '',
        nroTelefono: '',
        chapaMovil: '',
        aseguradora: '',
        porcentajeParticipacion: '',
        tipo: 'cuadrilla'
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, cuadrilla: null });

    useEffect(() => {
        fetchCuadrillas();
    }, []);

    const fetchCuadrillas = async () => {
        try {
            setLoading(true);
            const q = query(
                collection(firestore, 'cuadrillas_huawei'),
                orderBy('id', 'asc')
            );
            const querySnapshot = await getDocs(q);
            const cuadrillasData = [];
            
            querySnapshot.forEach((docSnap) => {
                cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            
            setCuadrillas(cuadrillasData);
        } catch (error) {
            console.error('Error al cargar cuadrillas:', error);
            showSnackbar('Error al cargar las cuadrillas', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (cuadrilla = null) => {
        if (cuadrilla) {
            setEditingCuadrilla(cuadrilla);
            setFormData({
                numero: cuadrilla.numero || '',
                nroTelefono: cuadrilla.nroTelefono || '',
                chapaMovil: cuadrilla.chapaMovil || '',
                aseguradora: cuadrilla.aseguradora || '',
                porcentajeParticipacion: cuadrilla.porcentajeParticipacion || '',
                tipo: cuadrilla.tipo || 'cuadrilla'
            });
        } else {
            setEditingCuadrilla(null);
            setFormData({
                numero: '',
                nroTelefono: '',
                chapaMovil: '',
                aseguradora: '',
                porcentajeParticipacion: '',
                tipo: 'cuadrilla'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCuadrilla(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            // Validaciones
            if (!formData.numero) {
                showSnackbar('Nombre es obligatorio', 'warning');
                return;
            }

            const dataToSave = {
                numero: formData.numero,
                nroTelefono: formData.nroTelefono,
                chapaMovil: formData.chapaMovil,
                aseguradora: formData.aseguradora,
                porcentajeParticipacion: formData.porcentajeParticipacion,
                tipo: formData.tipo
            };

            if (editingCuadrilla) {
                // Actualizar cuadrilla existente
                const docRef = doc(firestore, 'cuadrillas_huawei', editingCuadrilla.docId);
                await updateDoc(docRef, dataToSave);
                showSnackbar('Cuadrilla actualizada exitosamente', 'success');
            } else {
                // Crear nueva cuadrilla
                // Obtener el ID más alto para asignar el siguiente
                let maxId = 0;
                if (cuadrillas.length > 0) {
                    maxId = Math.max(...cuadrillas.map(c => parseInt(c.id) || 0));
                }
                
                const newId = maxId + 1;
                dataToSave.id = newId;
                
                await setDoc(doc(firestore, 'cuadrillas_huawei', newId.toString()), dataToSave);
                showSnackbar('Cuadrilla creada exitosamente', 'success');
            }

            handleCloseDialog();
            fetchCuadrillas();
        } catch (error) {
            console.error('Error al guardar cuadrilla:', error);
            showSnackbar('Error al guardar la cuadrilla', 'error');
        }
    };

    const handleDelete = async (cuadrilla) => {
        setDeleteConfirm({ open: true, cuadrilla });
    };

    const confirmDelete = async () => {
        try {
            const docRef = doc(firestore, 'cuadrillas_huawei', deleteConfirm.cuadrilla.docId);
            await deleteDoc(docRef);
            showSnackbar('Cuadrilla eliminada exitosamente', 'success');
            setDeleteConfirm({ open: false, cuadrilla: null });
            fetchCuadrillas();
        } catch (error) {
            console.error('Error al eliminar cuadrilla:', error);
            showSnackbar('Error al eliminar la cuadrilla', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const filteredCuadrillas = cuadrillas.filter(cuadrilla => {
        const searchLower = searchTerm.toLowerCase();
        return cuadrilla.numero?.toLowerCase().includes(searchLower);
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Cuadrillas - Huawei
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Nueva Cuadrilla
                    </Button>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Buscar por nombre"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Ej: Cuadrilla 1, Depósito Central..."
                    />
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Chip label={`Total: ${filteredCuadrillas.length} cuadrillas`} color="primary" />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Teléfono</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Chapa Móvil</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Aseguradora</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>% Participación</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCuadrillas.map((cuadrilla) => (
                                <TableRow 
                                    key={cuadrilla.docId}
                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                >
                                    <TableCell>{cuadrilla.id}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={cuadrilla.tipo === 'deposito' ? 'Depósito' : 'Cuadrilla'}
                                            color={cuadrilla.tipo === 'deposito' ? 'warning' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{cuadrilla.numero}</TableCell>
                                    <TableCell>{cuadrilla.nroTelefono}</TableCell>
                                    <TableCell>{cuadrilla.chapaMovil}</TableCell>
                                    <TableCell>{cuadrilla.aseguradora}</TableCell>
                                    <TableCell>{cuadrilla.porcentajeParticipacion}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleOpenDialog(cuadrilla)}
                                            title="Editar"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(cuadrilla)}
                                            title="Eliminar"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredCuadrillas.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchTerm ? `No se encontraron resultados para "${searchTerm}"` : 'No hay cuadrillas registradas. Crea una nueva cuadrilla para comenzar.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog para Crear/Editar */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingCuadrilla ? 'Editar Cuadrilla' : 'Nueva Cuadrilla'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nombre *"
                                    name="numero"
                                    value={formData.numero}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Cuadrilla 1, Depósito Central"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo *"
                                    name="tipo"
                                    value={formData.tipo}
                                    onChange={handleInputChange}
                                >
                                    {tiposCuadrilla.map((tipo) => (
                                        <MenuItem key={tipo} value={tipo}>
                                            {tipo === 'deposito' ? 'Depósito' : 'Cuadrilla'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Teléfono"
                                    name="nroTelefono"
                                    value={formData.nroTelefono}
                                    onChange={handleInputChange}
                                    placeholder="+595..."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Chapa Móvil"
                                    name="chapaMovil"
                                    value={formData.chapaMovil}
                                    onChange={handleInputChange}
                                    placeholder="ABC-123"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Aseguradora"
                                    name="aseguradora"
                                    value={formData.aseguradora}
                                    onChange={handleInputChange}
                                    placeholder="Nombre de la aseguradora"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="% Participación"
                                    name="porcentajeParticipacion"
                                    type="number"
                                    value={formData.porcentajeParticipacion}
                                    onChange={handleInputChange}
                                    placeholder="0-100"
                                    inputProps={{ min: 0, max: 100 }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        color="primary"
                    >
                        {editingCuadrilla ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, cuadrilla: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar la cuadrilla "{deleteConfirm.cuadrilla?.numero}"?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, cuadrilla: null })}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListCuadrillas;